import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI } from '../../utils/api';
import toast from 'react-hot-toast';
import { 
  UserPlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../Common/LoadingSpinner';
import { SCHOOL_LIST, DEFAULT_SCHOOL } from '../../constants/schools';

const AdminUserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showPassword, setShowPassword] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'STUDENT',
    grade: '1',
    schoolName: DEFAULT_SCHOOL
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getUsers();
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // 수정
        await userAPI.updateUser(editingUser.id, {
          username: formData.username,
          email: formData.email,
          gradeLevel: formData.grade ? `${formData.grade}학년` : null,
          schoolName: formData.schoolName
        });
        toast.success('사용자 정보가 수정되었습니다! ✏️');
      } else {
        // 생성
        const userData = {
          ...formData,
          grade: parseInt(formData.grade)
        };
        
        await userAPI.createUser(userData);
        toast.success('사용자 계정이 생성되었습니다! 🎉');
      }
      
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error creating/updating user:', error);
      toast.error(error.response?.data?.error || '작업에 실패했습니다.');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('정말로 이 사용자를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await userAPI.deleteUser(userId);
      toast.success('사용자가 삭제되었습니다.');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('사용자 삭제에 실패했습니다.');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'STUDENT',
      grade: '1',
      schoolName: DEFAULT_SCHOOL
    });
    setEditingUser(null);
    setShowCreateModal(false);
    setShowPassword(false);
  };

  const handleEdit = (userData) => {
    setEditingUser(userData);
    setFormData({
      username: userData.username,
      email: userData.email || '',
      password: '',
      role: userData.role,
      grade: userData.gradeLevel ? userData.gradeLevel.replace('학년', '') : '1',
      schoolName: userData.schoolName || DEFAULT_SCHOOL
    });
    setShowCreateModal(true);
  };

  const filteredUsers = users.filter(userData => {
    const matchesSearch = userData.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (userData.email && userData.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesRole = filterRole === 'all' || userData.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
    toast.success('랜덤 비밀번호가 생성되었습니다! 🎲');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('CSV 파일만 업로드 가능합니다.');
        return;
      }
      setUploadFile(file);
    }
  };

  const processBulkUpload = async () => {
    if (!uploadFile) {
      toast.error('파일을 선택해주세요.');
      return;
    }

    setUploadProgress(0);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const rows = text.split('\n').filter(row => row.trim());
        const headers = rows[0].split(',').map(h => h.trim());
        
        // 헤더 검증
        const requiredHeaders = ['username', 'email', 'password', 'role'];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        
        if (missingHeaders.length > 0) {
          toast.error(`필수 헤더가 누락되었습니다: ${missingHeaders.join(', ')}`);
          return;
        }

        const users = [];
        for (let i = 1; i < rows.length; i++) {
          const values = rows[i].split(',').map(v => v.trim());
          const user = {};
          headers.forEach((header, index) => {
            user[header] = values[index] || '';
          });

          if (user.username && user.password && user.role) {
            // 학년 정보 처리
            if (user.role === 'STUDENT' && user.grade) {
              user.grade = parseInt(user.grade);
            }
            users.push(user);
          }
        }

        if (users.length === 0) {
          toast.error('유효한 사용자 데이터가 없습니다.');
          return;
        }

        // 사용자 일괄 생성
        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < users.length; i++) {
          try {
            await userAPI.createUser(users[i]);
            successCount++;
          } catch (error) {
            console.error(`사용자 생성 실패 (${users[i].username}):`, error);
            failCount++;
          }
          setUploadProgress(Math.round(((i + 1) / users.length) * 100));
        }

        toast.success(`일괄 등록 완료! 성공: ${successCount}명, 실패: ${failCount}명`);
        setShowBulkUpload(false);
        setUploadFile(null);
        setUploadProgress(0);
        fetchUsers();
      } catch (error) {
        console.error('파일 처리 중 오류:', error);
        toast.error('파일 처리 중 오류가 발생했습니다.');
      }
    };

    reader.readAsText(uploadFile, 'UTF-8');
  };

  const downloadTemplate = () => {
    const template = 'username,email,password,role,grade\n' +
      'student1,student1@school.com,password123!,STUDENT,1\n' +
      'student2,student2@school.com,password123!,STUDENT,4\n' +
      'teacher1,teacher1@school.com,teacher123!,TEACHER,\n' +
      'admin1,admin1@school.com,admin123!,ADMIN,';
    
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'user_upload_template.csv';
    link.click();
  };

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'STUDENT':
        return '학생';
      case 'TEACHER':
        return '교사';
      case 'ADMIN':
        return '관리자';
      default:
        return role;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" message="사용자 목록을 불러오는 중..." />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">사용자 관리</h1>
        <p className="text-gray-600">모든 사용자 계정을 생성하고 관리할 수 있습니다.</p>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="사용자 이름 또는 이메일로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">전체 역할</option>
            <option value="ADMIN">관리자</option>
            <option value="TEACHER">교사</option>
            <option value="STUDENT">학생</option>
          </select>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <UserPlusIcon className="h-5 w-5" />
            사용자 추가
          </button>
          <button
            onClick={() => setShowBulkUpload(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            <ArrowUpTrayIcon className="h-5 w-5" />
            일괄 등록
          </button>
        </div>
      </div>

      {/* 사용자 목록 */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">등록된 사용자가 없습니다.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    사용자명
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    이메일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    역할
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    학년
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    학교
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    가입일
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((userData) => (
                  <tr key={userData.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 font-medium">
                            {userData.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {userData.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {userData.email || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        userData.role === 'ADMIN' ? 'bg-purple-100 text-purple-800' :
                        userData.role === 'TEACHER' ? 'bg-yellow-100 text-yellow-800' :
                        userData.role === 'STUDENT' ? 'bg-green-100 text-green-800' : 
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {getRoleDisplay(userData.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {userData.gradeLevel || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-sm text-gray-900">
                          {userData.schoolName || DEFAULT_SCHOOL}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        userData.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {userData.isActive ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(userData.createdAt).toLocaleDateString('ko-KR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(userData)}
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                        title="수정"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      {userData.id !== user.id && (
                        <button
                          onClick={() => handleDelete(userData.id)}
                          className="text-red-600 hover:text-red-900"
                          title="삭제"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 생성/수정 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 my-8 max-h-[95vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingUser ? '사용자 정보 수정' : '새 사용자 추가'}
              </h3>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleCreateOrUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  사용자명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  이메일
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="선택사항"
                />
              </div>

              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    비밀번호 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3"
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={generateRandomPassword}
                      className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      랜덤 생성
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    💡 최소 6자 이상 권장
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  역할 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="STUDENT">학생</option>
                  <option value="TEACHER">교사</option>
                  <option value="ADMIN">관리자</option>
                </select>
              </div>

              {formData.role === 'STUDENT' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    학년
                  </label>
                  <select
                    value={formData.grade}
                    onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">1학년</option>
                    <option value="2">2학년</option>
                    <option value="3">3학년</option>
                    <option value="4">4학년</option>
                    <option value="5">5학년</option>
                    <option value="6">6학년</option>
                  </select>
                </div>
              )}

              <div data-testid="school-selection-section">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  학교 <span className="text-red-500">*</span>
                </label>
                <div className="text-xs text-green-600 mb-1">🔍 학교 선택 섹션 (디버깅용)</div>
                <select
                  data-testid="school-dropdown"
                  value={formData.schoolName}
                  onChange={(e) => setFormData({ ...formData, schoolName: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-yellow-50"
                  required
                >
                  <option value="">학교를 선택하세요</option>
                  {SCHOOL_LIST.map((school) => (
                    <option key={school} value={school}>
                      {school}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-gray-500 mt-1">현재 선택: {formData.schoolName || '없음'}</div>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <CheckIcon className="h-5 w-5" />
                  {editingUser ? '수정하기' : '생성하기'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 일괄 업로드 모달 */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                사용자 일괄 등록
              </h3>
              <button
                onClick={() => {
                  setShowBulkUpload(false);
                  setUploadFile(null);
                  setUploadProgress(0);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  CSV 파일을 통해 여러 사용자를 한 번에 등록할 수 있습니다.
                </p>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  <DocumentTextIcon className="h-4 w-4" />
                  템플릿 다운로드
                </button>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <ArrowUpTrayIcon className="h-12 w-12 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    클릭하여 CSV 파일 선택
                  </span>
                </label>
                {uploadFile && (
                  <p className="mt-2 text-sm text-gray-900 font-medium">
                    선택된 파일: {uploadFile.name}
                  </p>
                )}
              </div>

              {uploadProgress > 0 && (
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>업로드 진행중...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={processBulkUpload}
                  disabled={!uploadFile || uploadProgress > 0}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckIcon className="h-5 w-5" />
                  업로드
                </button>
                <button
                  onClick={() => {
                    setShowBulkUpload(false);
                    setUploadFile(null);
                    setUploadProgress(0);
                  }}
                  disabled={uploadProgress > 0}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagement;